import { ICourseData, ICourseEnquiry, IIsCourseBought } from '@/interfaces/courseDescription';
import { baseApiUrl, domainName } from '@/utils/common/env.constants';
import { randomId } from '@pwskills/rachnaui/utils';
import { domainNameEnums, saasDomains } from '@/utils/Domain';
import { saasDomainprefixUrl, skillsDomainprefixUrl } from '@/utils/Configuration/utils';
import { get, post } from './index';

interface ICourseActivity {
  courseId: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
}

const prefixUrl = saasDomains.includes(domainName as domainNameEnums)
  ? saasDomainprefixUrl
  : skillsDomainprefixUrl;

class CourseDescription {
  static async getCourseDescription(slug: string, userId?: string) {
    return get<ICourseData>(
      `/course/${slug}?isSlug=true&random_id=${randomId()}${userId ? `&userId=${userId}` : ''}`,
      undefined,
      `${baseApiUrl}/v2`
    );
  }

  static async isCourseBought(courseId: string) {
    return get<IIsCourseBought>(
      `/learn/user/isCourseBought/${courseId}`,
      undefined,
      `${baseApiUrl}/${prefixUrl}`
    );
  }

  static async trackUserCourseActivity(data: ICourseActivity) {
    return post(
      `/course/trackUserCourseActivity/${data?.courseId}`,
      {
        activity: 'explore',
      },
      {
        params: {
          source: data?.utm_source,
          medium: data?.utm_medium,
          campaign: data?.utm_campaign,
        },
      },
      `${baseApiUrl}/${prefixUrl}`
    );
  }

  // static async getUserRatings(courseId?: string) {
  //   if (!courseId) return null;
  //   return get<IUserRating>(`/rating/${courseId}`);
  // }

  // static async addUserRatings(data: { rating: number; courseId?: string }) {
  //   return post<IUserRating>('/rating', data);
  // }

  static async courseEnquiry(
    courseId: string,
    token: string,
    data: {
      fullName: string;
      email?: string;
      isdCode: string;
      phoneNumber: string;
    }
  ) {
    return post<ICourseEnquiry>(
      `course/sendCourseLeadToLeadSquared/${courseId}`,
      {
        ...data,
        'g-recaptcha-response': token,
      },
      undefined,
      `${baseApiUrl}/${prefixUrl}`
    );
  }

  static async courseRedirectionURL(redirectionUrl: string, queryParams?: string) {
    return get<{ data: string }>(
      `/v2/course/redirection-handler?redirectionUrl=${encodeURIComponent(
        redirectionUrl
      )}&queryParams=${queryParams?.split('&').join(',')}`,
      undefined,
      `${baseApiUrl}`
    );
  }

  static async getServerSideRedirectUrl(redirectionUrl: string): Promise<string | null> {
    const toComparablePath = (value: string) => {
      try {
        const parsed = new URL(value);
        return `${parsed.origin}${parsed.pathname.replace(/\/+$/, '')}`;
      } catch {
        return value.split('?')[0].replace(/\/+$/, '');
      }
    };

    const checkUrl = async (url: string): Promise<string | null> => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      try {
        const apiUrl = `${baseApiUrl}/v2/course/redirection-handler?redirectionUrl=${encodeURIComponent(
          url
        )}&queryParams=`;
        const response = await fetch(apiUrl, {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!response.ok) return null;
        const result: { data?: string | Record<string, never> } = await response.json();
        if (
          result?.data &&
          typeof result.data === 'string' &&
          result.data.length > 0 &&
          toComparablePath(result.data) !== toComparablePath(url)
        ) {
          return result.data;
        }
      } catch (error: unknown) {
        clearTimeout(timeoutId);
        // eslint-disable-next-line no-console
        console.error(`Redirect check failed for ${url}:`, error);
      }
      return null;
    };

    const result = await checkUrl(redirectionUrl);
    if (result) return result;

    const altUrl = redirectionUrl.endsWith('/')
      ? redirectionUrl.slice(0, -1)
      : `${redirectionUrl}/`;
    return checkUrl(altUrl);
  }
}

export default CourseDescription;
